/**
 * Icon catalogue (Plan 01-16, Task 3).
 *
 * 21 named UI icons, each drawn in `currentColor` so `text-ink-2` /
 * `text-primary` (etc.) tints them. Solid glyphs fill; outline
 * glyphs (checkmark, chevrons, plus) stroke — see `mkStroke`.
 * Authored from UI-SPEC §"Component Inventory — Primitives" + the
 * Stell design handoff (`primitives.jsx` Icon object). The handoff zip is not
 * present at executor time (per STATE.md Plan 09 decision); UI-SPEC is
 * authoritative.
 *
 * The 21 icons are: navigation (Home, Cal, Cart, Chat), actions (Check,
 * Sparkle, ChevR, Plus, Mic, Moon), chore-specific (Flame, Bulb, Therm,
 * Vacuum, Dishes, Bed, Trash, Laundry, Plant, Dog, Book).
 *
 * All SVGs use a 24×24 viewBox so a single base size keeps stroke
 * weights visually consistent. Each component honours `size`,
 * `className`, and a `title` prop for the rare case where the icon
 * stands alone (no adjacent text). Default is decorative
 * (`aria-hidden`); pass `title` to make it accessible.
 */

import type { ReactElement } from 'react';

interface IconProps {
  size?: number;
  className?: string;
  title?: string;
}

type IconComponent = (props: IconProps) => ReactElement;

/**
 * Build an icon component from a path-d string. Bundles the title
 * + aria-hidden contract so callers don't have to repeat it.
 */
function mkIcon(pathD: string, viewBox = '0 0 24 24'): IconComponent {
  const Component = ({ size = 24, className, title }: IconProps) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox={viewBox}
      fill="currentColor"
      className={className}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {title ? <title>{title}</title> : null}
      <path d={pathD} />
    </svg>
  );
  return Component;
}

/**
 * Build an icon component from raw SVG children (used when one path is
 * insufficient — multi-shape glyphs like the laundry basket).
 */
function mkMulti(children: ReactElement, viewBox = '0 0 24 24'): IconComponent {
  const Component = ({ size = 24, className, title }: IconProps) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox={viewBox}
      fill="currentColor"
      className={className}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {title ? <title>{title}</title> : null}
      {children}
    </svg>
  );
  return Component;
}

// Stroke style for outlined glyphs that are too thin as fills.
const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/**
 * Build an icon component from an *outlined* path-d string — a polyline
 * that only reads as its glyph when stroked (checkmark, chevron, plus).
 * Filling those closes the path into a solid wedge (or, for the pure
 * cross of `Plus`, draws nothing at all), so they take `STROKE` instead
 * of `mkIcon`'s `fill="currentColor"`.
 */
function mkStroke(pathD: string, viewBox = '0 0 24 24'): IconComponent {
  const Component = ({ size = 24, className, title }: IconProps) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox={viewBox}
      fill="none"
      className={className}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {title ? <title>{title}</title> : null}
      <path d={pathD} {...STROKE} />
    </svg>
  );
  return Component;
}

export const Icon = {
  // ------ Actions / status ------
  Check: mkStroke('M20 6L9 17l-5-5'),
  Sparkle: mkIcon(
    'M12 2l1.5 4.5L18 8l-4.5 1.5L12 14l-1.5-4.5L6 8l4.5-1.5z M19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8z',
  ),
  ChevR: mkStroke('M9 6l6 6-6 6'),
  Plus: mkStroke('M12 5v14M5 12h14'),
  // #152: «Færre» i Stepper.
  Minus: mkStroke('M5 12h14'),
  Mic: mkMulti(
    <>
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path
        d="M5 11a7 7 0 0 0 14 0M12 18v3"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
      />
    </>,
  ),
  Moon: mkIcon('M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z'),

  // ------ Navigation ------
  Cal: mkMulti(
    <>
      <rect
        x="3"
        y="5"
        width="18"
        height="16"
        rx="2"
        {...STROKE}
      />
      <path d="M3 9h18" {...STROKE} />
      <path d="M8 3v4M16 3v4" {...STROKE} />
    </>,
  ),
  Cart: mkMulti(
    <>
      <path
        d="M3 4h2l2.5 11h12L22 7H6"
        {...STROKE}
      />
      <circle cx="9" cy="20" r="1.5" />
      <circle cx="18" cy="20" r="1.5" />
    </>,
  ),
  Home: mkIcon('M3 12l9-9 9 9v9a2 2 0 0 1-2 2h-4v-7h-6v7H5a2 2 0 0 1-2-2z'),
  Chat: mkIcon(
    'M4 4h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H8l-4 4z',
  ),
  // Person silhouette — the "Meg" nav tab (→ /settings) and any
  // member-self affordance. Head circle + shoulders, stroked to match Cal/Cart.
  User: mkMulti(
    <>
      <circle cx="12" cy="8" r="4" {...STROKE} />
      <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" {...STROKE} />
    </>,
  ),

  // ------ Chore-specific ------
  Flame: mkIcon(
    'M12 2c1 3 4 5 4 9a4 4 0 0 1-8 0c0-2 1-3 2-4-1 4 2 4 2 1 0-2 0-4 0-6z',
  ),
  Bulb: mkMulti(
    <>
      <path
        d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.5 10.9c.9.7 1.5 1.5 1.5 2.6v.5h4v-.5c0-1.1.6-1.9 1.5-2.6A6 6 0 0 0 12 3z"
        {...STROKE}
      />
    </>,
  ),
  Therm: mkMulti(
    <>
      <path
        d="M12 3a3 3 0 0 0-3 3v9.3a4.5 4.5 0 1 0 6 0V6a3 3 0 0 0-3-3z"
        {...STROKE}
      />
      <circle cx="12" cy="17" r="2" />
    </>,
  ),
  Vacuum: mkMulti(
    <>
      <circle cx="12" cy="13" r="7" {...STROKE} />
      <circle cx="12" cy="13" r="2" />
      <path d="M12 6V3M9 3h6" {...STROKE} />
    </>,
  ),
  Dishes: mkMulti(
    <>
      <ellipse cx="12" cy="9" rx="8" ry="2" {...STROKE} />
      <path d="M4 9c0 5 3 9 8 9s8-4 8-9" {...STROKE} />
      <path d="M9 13v3M15 13v3" {...STROKE} />
    </>,
  ),
  Bed: mkMulti(
    <>
      <path
        d="M2 18v-7a2 2 0 0 1 2-2h9a4 4 0 0 1 4 4v5"
        {...STROKE}
      />
      <path d="M2 14h20v4M22 18v3M2 18v3" {...STROKE} />
      <circle cx="7" cy="12" r="2" />
    </>,
  ),
  Trash: mkMulti(
    <>
      <path
        d="M4 6h16M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 6l1 14a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-14"
        {...STROKE}
      />
      <path d="M10 11v6M14 11v6" {...STROKE} />
    </>,
  ),
  Laundry: mkMulti(
    <>
      <rect
        x="4"
        y="3"
        width="16"
        height="18"
        rx="2"
        {...STROKE}
      />
      <circle cx="12" cy="14" r="4" {...STROKE} />
      <circle cx="8" cy="6" r="0.8" />
      <circle cx="11" cy="6" r="0.8" />
    </>,
  ),
  Plant: mkMulti(
    <>
      <path d="M12 22V12" {...STROKE} />
      <path
        d="M12 12c-3 0-5-2-5-5 3 0 5 2 5 5zM12 12c3 0 5-2 5-5-3 0-5 2-5 5z"
        fill="currentColor"
      />
      <path d="M7 22h10l-1-5H8z" {...STROKE} />
    </>,
  ),
  Dog: mkMulti(
    <>
      <path
        d="M5 7l1-3 3 1v3l3 1 3-1V4l3-1 1 3v6c0 4-3 7-7 7s-7-3-7-7z"
        {...STROKE}
      />
      <circle cx="9.5" cy="11" r="0.8" />
      <circle cx="14.5" cy="11" r="0.8" />
      <path d="M11 14h2" {...STROKE} />
    </>,
  ),
  Book: mkMulti(
    <>
      <path
        d="M4 4h6a3 3 0 0 1 3 3v13a2 2 0 0 0-2-2H4z"
        {...STROKE}
      />
      <path
        d="M20 4h-6a3 3 0 0 0-3 3v13a2 2 0 0 1 2-2h7z"
        {...STROKE}
      />
    </>,
  ),

  // ------ Phase 2 extensions (Plan 02-09) ------
  // 5-point crown silhouette for <CrownOverlay> weekly winner badge.
  Crown: mkIcon('M3 18l3-9 3 6 3-9 3 9 3-6 3 9H3z'),

  // ------ Phase 3 extensions (Plan 03-06) ------
  // Bell — event reminder icon on EventCard medium/full variants.
  Bell: mkIcon('M12 22a2 2 0 002-2h-4a2 2 0 002 2zm6-6V11a6 6 0 10-12 0v5l-2 2v1h16v-1l-2-2z'),
  // Repeat — recurring-event indicator on EventCard.
  Repeat: mkMulti(
    <>
      <path d="M17 1l4 4-4 4M3 11V9a4 4 0 014-4h14" {...STROKE} />
      <path d="M7 23l-4-4 4-4M21 13v2a4 4 0 01-4 4H3" {...STROKE} />
    </>,
  ),
  // ChevL — left navigation arrow (mirror of ChevR).
  ChevL: mkStroke('M15 18l-6-6 6-6'),
  // Camera body + lens for the photo-proof "Legg til bilde" CTA.
  Camera: mkMulti(
    <>
      <path
        d="M4 7h3l1.5-2h7L17 7h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2z"
        {...STROKE}
      />
      <circle cx="12" cy="13" r="4" {...STROKE} />
      <circle cx="12" cy="13" r="1.5" />
    </>,
  ),
  // Stroked X — bottom-sheet close, ReasonChipPicker "Annet" remove.
  XMark: mkMulti(
    <>
      <path d="M5 5l14 14M19 5l-14 14" {...STROKE} />
    </>,
  ),
  // Closed padlock — locked secret-achievement cells.
  Lock: mkMulti(
    <>
      <rect x="5" y="11" width="14" height="9" rx="2" {...STROKE} />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" {...STROKE} />
    </>,
  ),
  // Loving-cup trophy — personal-best card + leaderboard "Tidenes" tab.
  Trophy: mkMulti(
    <>
      <path
        d="M7 4h10v4a5 5 0 0 1-10 0V4z"
        {...STROKE}
      />
      <path d="M7 6H4a3 3 0 0 0 3 3M17 6h3a3 3 0 0 1-3 3" {...STROKE} />
      <path d="M10 13h4l-1 4h-2z" {...STROKE} />
      <path d="M8 21h8" {...STROKE} />
    </>,
  ),

  // ------ Phase 4 extensions (Plan 04-03) ------
  // WifiOff — offline-pending chip + top-of-route offline banner.
  // UI-SPEC §"Icon Set Extension" path-d (Lucide-equivalent).
  WifiOff: mkMulti(
    <>
      <path
        d="M5 12.55a11 11 0 0 1 14.08 0M1.42 9a16 16 0 0 1 21.16 0M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01M2 2l20 20"
        {...STROKE}
      />
    </>,
  ),

  // ------ Phase 5 extensions (Plan 05-07a) ------
  // Stop — InputRow stop-stream button. Circle outline + filled square inside.
  Stop: mkMulti(
    <>
      <circle cx={12} cy={12} r={9} {...STROKE} />
      <rect x={8} y={8} width={8} height={8} rx={1} fill="currentColor" />
    </>,
  ),
  // AlertShield — AllergyFilterBanner. Shield outline with exclamation inside.
  AlertShield: mkMulti(
    <>
      <path d="M12 2L4 6v6c0 4.4 3.4 8.5 8 10 4.6-1.5 8-5.6 8-10V6l-8-4z" {...STROKE} fill="none" />
      <path d="M12 8v4M12 14h.01" {...STROKE} strokeLinecap="round" />
    </>,
  ),
  // Send — InputRow send-message button. Arrow-up variant.
  Send: mkMulti(
    <>
      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" {...STROKE} />
    </>,
  ),

  // ------ Phase 6 extensions (Plan 06-01) ------
  // Database — admin "Retningslinjer" cylinder (three stacked ellipses).
  Database: mkMulti(
    <>
      <ellipse cx={12} cy={5.5} rx={7} ry={2.5} {...STROKE} />
      <path d="M5 5.5v6c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5v-6" {...STROKE} />
      <path d="M5 11.5v7c0 1.4 3.1 2.5 7 2.5s7-1.1 7-2.5v-7" {...STROKE} />
    </>,
  ),
  // Share — iOS share-sheet arrow-up icon for install walkthrough step 1.
  Share: mkMulti(
    <>
      <path d="M12 3v12" {...STROKE} />
      <path d="M8 7l4-4 4 4" {...STROKE} />
      <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" {...STROKE} />
    </>,
  ),
  // Shield — admin Retningslinjer section + InstallRoute privacy assurance.
  // Distinct from AlertShield: no internal exclamation, plain outline only.
  Shield: mkMulti(
    <>
      <path
        d="M12 3l8 3v6c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V6l8-3z"
        {...STROKE}
        fill="none"
      />
    </>,
  ),

  // ------ Hovedmenyen (#153) ------
  // Strektegningene fra skissene (design/screens/Stell_Hjem.dc.html), tegnet
  // 20 px i pillen og skinnen.
  NavHome: mkStroke('M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z'),
  NavTasks: mkStroke(
    'M8 4h8a4 4 0 0 1 4 4v8a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4zm.5 8 2.5 2.5 4.5-5',
  ),
  NavRewards: mkStroke(
    'M4.5 8h15a1 1 0 0 1 1 1v2.5a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1zM5 12.5V20h14v-7.5M12 8v12M12 8c-1.2-3-5-4-5-1.5C7 8 10 8 12 8zm0 0c1.2-3 5-4 5-1.5C17 8 14 8 12 8z',
  ),
  NavPlan: mkStroke(
    'M6.5 5h11a3 3 0 0 1 3 3v9.5a3 3 0 0 1-3 3h-11a3 3 0 0 1-3-3V8a3 3 0 0 1 3-3zM3.5 10h17M8 3v4M16 3v4',
  ),
  NavApprove: mkStroke('m5 12.5 4.5 4.5L19 7.5'),
  NavFamily: mkStroke(
    'M9 5.5a3 3 0 1 1 0 6 3 3 0 0 1 0-6zM3.5 19c.6-3 2.8-4.5 5.5-4.5s4.9 1.5 5.5 4.5M16 6a3 3 0 0 1 0 5.5M17.5 14.7c1.6.5 2.7 1.9 3 4.3',
  ),
  // Assistentknappen: firespiss-stjerne med et lite pluss.
  NavAssistant: mkStroke('M12 3.5l1.9 5.6 5.6 1.9-5.6 1.9-1.9 5.6-1.9-5.6-5.6-1.9 5.6-1.9zM19 3v3M17.5 4.5h3'),

  // ------ Assistent (#158) ------
  // Strektegningene fra design/screens/Stell_Assistent.dc.html: stjernen i
  // toppen, varseltrekanten i «Sjekk med en voksen», og ⋯ for menyen.
  AssistantMark: mkStroke('M12 3.5l1.9 5.6 5.6 1.9-5.6 1.9-1.9 5.6-1.9-5.6-5.6-1.9 5.6-1.9z'),
  Warn: mkStroke('M12 4 3 19.5h18zM12 10v4M12 17v.01'),
  More: mkIcon(
    'M6 10.5a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zm6 0a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zm6 0a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3z',
  ),
  // Brikken «Hentet data (N verktøy)» under et svar.
  ToolList: mkStroke('M4 6.5h16M4 12h16M4 17.5h10'),
  // «Rediger» på utkastkortene (Stell_Utkast).
  Edit: mkStroke('M4 20h4L19 9l-4-4L4 16zM13.5 6.5l4 4'),

  // ------ Lommebok (#156) ------
  // Strektegningene i statistikkflisene (design/screens/Stell_Lommebok.dc.html).
  StatStreak: mkStroke(
    'M12 3c.8 3.6 5.5 5.4 5.5 10.2a5.5 5.5 0 0 1-11 0c0-2.6 1.4-3.9 1.9-6 1.6 1 2.6 2.6 2.7 4.3C12 9.6 12.4 6.3 12 3z',
  ),
  StatBest: mkStroke('M3.5 8.5 7.5 12 12 5.5l4.5 6.5 4-3.5-1.8 10H5.3z'),
  StatFreeze: mkStroke('M12 3v18M4.2 7.5l15.6 9M19.8 7.5l-15.6 9'),
  StatWeek: mkStroke('M4 20V10M10 20V4M16 20v-7M22 20H2'),

  // ------ Tema i Meg (#159, steg 9d, Stell_Meg) ------
  // «Etter solen», «Lyst» og «Mørkt» i temavelgeren.
  ThemeAuto: mkStroke(
    'M12 3v2M12 19v2M5 12H3M21 12h-2M6.3 6.3 5 5M19 19l-1.3-1.3M6.3 17.7 5 19M19 5l-1.3 1.3M12 8a4 4 0 1 1 0 8 4 4 0 0 1 0-8z',
  ),
  ThemeLight: mkStroke('M12 7.5a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9zM12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2'),
  ThemeDark: mkStroke('M19.5 14.5A7.5 7.5 0 0 1 9.5 4.5a7.5 7.5 0 1 0 10 10z'),

  // ------ Innlogging (#159, steg 9g, Stell_Innlogging) ------
  // Klokka i låsebanneret «For mange forsøk».
  Clock: mkStroke('M12 3.5a8.5 8.5 0 1 1 0 17 8.5 8.5 0 0 1 0-17zM12 7.5V12l3 2'),

  // ------ Tilbakemelding i toppen (#187) ------
  // Snakkeboble med utropstegn: «si fra om noe her».
  Feedback: mkStroke(
    'M5 4.5h14A1.5 1.5 0 0 1 20.5 6v9a1.5 1.5 0 0 1-1.5 1.5h-9l-4.5 3.5v-3.5H5A1.5 1.5 0 0 1 3.5 15V6A1.5 1.5 0 0 1 5 4.5zM12 8v3.5M12 14v.01',
  ),
} satisfies Record<string, IconComponent>;

export type IconName = keyof typeof Icon;
